import { IconFileAttachmentOutlined, IconTrashOutlined } from '@repo/monochrome-icons';
import {
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Button } from 'react-aria-components';
import { useIntl } from 'react-intl';
import cx from 'clsx';
import { useFormatBytes } from '../../hooks/use-format-bytes';
import styles from './attachment.module.css';
import { Processing } from './processing/processing';
import { Uploading } from './uploading/uploading';

interface AttachmentProps {
  className?: string;
  description?: ReactNode;
  fileSize: number;
  fileUrl?: string;
  fileName: string;
  fileThumbnail?: string;
  icon?: ComponentType<{ className?: string }>;
  onRemove?: () => void;
  onPreview?: () => void;
  uploadState?: 'uploaded' | 'processing' | 'error' | 'uploading';
  uploadProcessingStateTitle?: string;
  uploadProgress?: number;
  errorMessage?: string | null;
}

function AttachmentPreviewIcon({
  hasError,
  Icon = IconFileAttachmentOutlined,
  thumbnail,
  fileName,
}: {
  hasError: boolean;
  Icon?: ComponentType<{ className?: string }>;
  thumbnail?: string;
  fileName: string;
}): ReactElement {
  const [imageError, setImageError] = useState(false);

  return (
    <span
      className={cx(styles.illustration, hasError && styles.error, thumbnail && styles.thumbnail)}
    >
      {thumbnail && !imageError ? (
        <img
          alt={fileName}
          onError={() => {
            setImageError(true);
          }}
          src={thumbnail}
        />
      ) : (
        <Icon className={styles.icon} />
      )}
    </span>
  );
}

/**
 * @deprecated Use `AttachmentV2` from `@repo/design-system-kit` instead.
 * For file upload flows, consider using `DocumentUploader` which wraps `AttachmentV2`
 * with built-in upload management.
 */
export function Attachment({
  className,
  description,
  fileSize,
  fileUrl,
  fileName,
  fileThumbnail,
  icon,
  onRemove,
  onPreview,
  uploadState = 'uploaded',
  uploadProcessingStateTitle,
  uploadProgress,
  errorMessage = null,
  ...props
}: AttachmentProps): ReactElement {
  const { formatMessage } = useIntl();
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(null);
  const { formatBytes } = useFormatBytes();
  const fileSizeFormatted = formatBytes(fileSize);
  const url = fileUrl ?? '';
  const progress = useMemo(() => {
    return uploadProgress ? Math.round(uploadProgress) : 0;
  }, [uploadProgress]);

  useEffect(() => {
    if (uploadState === 'error' && errorMessage) {
      setLocalErrorMessage(errorMessage);
    }
  }, [formatMessage, uploadState, errorMessage]);

  if (uploadState === 'processing') {
    return (
      <Processing
        className={className}
        description={description}
        title={uploadProcessingStateTitle}
        {...props}
      />
    );
  }

  if (uploadState === 'uploading') {
    return (
      <Uploading
        className={className}
        hasPreview={Boolean(onPreview)}
        progress={progress}
        title={fileName}
        {...props}
      />
    );
  }

  return (
    <div className={cx(styles.attachment, className)} {...props}>
      {onPreview ? (
        <Button
          aria-label={formatMessage({ id: 'a11y-buttons-open-file-aria-label' })}
          className={styles.previewButton}
          data-test-preview-button-link
          onPress={onPreview}
        >
          <AttachmentPreviewIcon
            Icon={icon}
            fileName={fileName}
            hasError={Boolean(localErrorMessage)}
            thumbnail={fileThumbnail}
          />
        </Button>
      ) : (
        <>
          {url ? (
            <a
              aria-label={formatMessage({ id: 'a11y-buttons-open-file-aria-label' })}
              href={url}
              rel="noreferrer"
              target="_blank"
            >
              <AttachmentPreviewIcon
                Icon={icon}
                fileName={fileName}
                hasError={Boolean(localErrorMessage)}
                thumbnail={fileThumbnail}
              />
            </a>
          ) : (
            <AttachmentPreviewIcon
              Icon={icon}
              fileName={fileName}
              hasError={Boolean(localErrorMessage)}
              thumbnail={fileThumbnail}
            />
          )}
        </>
      )}
      <div className={styles.info}>
        {onPreview ? (
          <Button
            aria-label={formatMessage({ id: 'a11y-buttons-open-file-aria-label' })}
            className={cx('body-standard', styles.previewText)}
            data-testid="file-name"
            onPress={onPreview}
          >
            {fileName}
          </Button>
        ) : (
          <p className="body-standard" data-testid="file-name">
            {fileName}
          </p>
        )}
        {!localErrorMessage && (
          <p className={cx('body-standard', styles.fileSize)} data-testid="file-size">
            {description || fileSizeFormatted}
          </p>
        )}
        {Boolean(localErrorMessage) && (
          <p className={cx('caption', styles.errorMessage)} data-testid="error-message">
            {localErrorMessage}
          </p>
        )}
      </div>
      {Boolean(onRemove) && (
        <Button
          aria-label={formatMessage({ id: 'a11y-buttons-remove-file-aria-label' })}
          className={styles.removeButton}
          onPress={onRemove}
        >
          <IconTrashOutlined className={styles.icon} />
        </Button>
      )}
    </div>
  );
}
